import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, BookOpen, Brain, Headphones, MessageCircle, Shield,
  ArrowRight, Check, ExternalLink,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SURVEY_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSfOI9LJC4bj67lEqycm5euGFxe4znTJdLwLwmd58fUA6CE1-Q/viewform";

const FEATURES = [
  { icon: BookOpen, title: "Revision books.", body: "Upload a topic's notes and get them back as clear chapters with headings, key definitions and the concepts that matter, ready to revise from." },
  { icon: Brain, title: "Flashcards and quizzes.", body: "Auto-made from their own notes, with spaced repetition and a test mode that tells them what they actually know." },
  { icon: Headphones, title: "Revision podcasts.", body: "Turn a subject into audio they can listen to on the bus or before bed, in a tone that suits them." },
  { icon: MessageCircle, title: "A study assistant.", body: "A tutor-style chat that answers questions on their own notes and makes practice questions, one subject at a time." },
];

export default function WaitlistLanding() {
  const { user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("parent");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Logged-in users still get the regular app home.
  if (!loading && user) {
    return <Navigate to={user.wizard_completed_at ? "/family" : "/welcome"} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/waitlist", { email: email.trim(), role });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again?");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#FAFAFA] dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100" data-testid="waitlist-landing">
      {/* Nav */}
      <header className="nb-border border-l-0 border-r-0 border-t-0 bg-white dark:bg-zinc-900">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="font-display font-black text-xl tracking-tight flex items-center gap-2">
            <span className="inline-block w-7 h-7 rounded-lg bg-[#FFE066] nb-border" />
            ReviseAI
          </div>
          <Link to="/login" data-testid="nav-signin" className="nb-border rounded-xl px-3 py-1.5 text-xs font-bold hover:nb-shadow">
            Sign in
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 sm:pt-24 pb-14">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <div className="inline-flex items-center gap-2 nb-border rounded-full px-3 py-1 bg-[#FFE066] text-black mb-6">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={3} />
            <span className="text-[11px] font-extrabold uppercase tracking-wider">Built for GCSE · AQA · Edexcel · OCR</span>
          </div>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black tracking-tighter max-w-4xl leading-[1.02]">
            Turn your teen&apos;s messy notes into{" "}
            <span className="bg-[#9DFFB0] dark:bg-[#68D391] px-3 nb-border rounded-2xl inline-block text-black">
              revision that actually works.
            </span>
          </h1>
          <p className="text-lg sm:text-xl mt-6 text-zinc-700 dark:text-zinc-300 font-medium max-w-2xl">
            ReviseAI takes their notes and turns them into revision books, flashcards, quizzes and audio, organised by subject. So revision gets done, instead of put off.
          </p>
          <a
            href="#waitlist-form"
            data-testid="hero-cta"
            className="nb-button inline-flex items-center gap-2 bg-black text-white dark:bg-white dark:text-black px-6 py-4 mt-8 text-lg font-display"
          >
            Join the waitlist <ArrowRight className="w-5 h-5" strokeWidth={3} />
          </a>
        </motion.div>
      </section>

      {/* Problem */}
      <section className="bg-white dark:bg-zinc-900 border-y-2 border-black dark:border-zinc-700">
        <div className="max-w-3xl mx-auto px-6 py-14 sm:py-20">
          <p className="text-xl sm:text-2xl leading-relaxed font-medium">
            You already know revision matters. The hard part is getting it to actually happen. Notes are scattered, nobody knows where to start, and &ldquo;I&apos;m revising&rdquo; too often means a highlighter and a phone. ReviseAI does the organising, so your teen can get on with the learning.
          </p>
        </div>
      </section>

      {/* What it does */}
      <section className="max-w-6xl mx-auto px-6 py-16 sm:py-24">
        <div className="label-caps mb-3">What it does</div>
        <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tighter mb-10 max-w-3xl">
          Everything a revision session actually needs.
        </h2>
        <div className="grid sm:grid-cols-2 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="nb-card p-6 bg-white dark:bg-zinc-900"
            >
              <div className="w-10 h-10 nb-border rounded-xl flex items-center justify-center mb-3 bg-[#D4C4FB] dark:bg-[#B794F4] text-black">
                <f.icon className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <h3 className="font-display text-xl font-extrabold mb-1.5">{f.title}</h3>
              <p className="text-zinc-700 dark:text-zinc-300 font-medium leading-relaxed text-[15px]">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Founding-member offer */}
      <section className="bg-[#FFB38E] dark:bg-[#F6AD55] text-black border-y-2 border-black">
        <div className="max-w-3xl mx-auto px-6 py-14 sm:py-20">
          <div className="label-caps mb-3">Founding-member offer</div>
          <p className="text-xl sm:text-2xl font-display font-black tracking-tight leading-snug">
            We launch in January, right when revision gets real. Join the waitlist now as a founding member and the first 100 families get 30 days free instead of 7. We will also ask you a few quick questions so we build the bits you actually want.
          </p>
        </div>
      </section>

      {/* Form */}
      <section id="waitlist-form" className="max-w-xl mx-auto px-6 py-16 sm:py-24">
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mb-6 text-center">
          Be first in, at the founding price.
        </h2>

        {!done ? (
          <form onSubmit={submit} className="nb-card p-6 sm:p-8 space-y-5" data-testid="waitlist-form">
            <div>
              <label className="label-caps block mb-2" htmlFor="waitlist-email">Email address</label>
              <input
                id="waitlist-email"
                data-testid="waitlist-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="nb-input"
              />
            </div>
            <div>
              <label className="label-caps block mb-2">I&apos;m a</label>
              <div
                data-testid="waitlist-role-parent"
                className="nb-border rounded-xl px-4 py-3 font-bold text-sm bg-[#D4C4FB] dark:bg-[#B794F4] nb-shadow text-black text-center"
              >
                Parent
              </div>
            </div>
            <button
              type="submit"
              data-testid="waitlist-submit"
              disabled={submitting || !email.trim()}
              className="nb-button w-full py-4 bg-black text-white dark:bg-white dark:text-black font-display text-lg flex items-center justify-center gap-2"
            >
              {submitting ? "Joining…" : "Join the waitlist"}
              {!submitting && <ArrowRight className="w-5 h-5" strokeWidth={3} />}
            </button>
            <p className="text-xs text-zinc-500 font-medium text-center">
              No card needed. We will email you the moment it opens.
            </p>
          </form>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="nb-card p-8 text-center bg-[#9DFFB0] dark:bg-[#68D391] text-black"
            data-testid="waitlist-thanks"
          >
            <Check className="w-10 h-10 mx-auto mb-3" strokeWidth={3} />
            <h3 className="font-display text-2xl font-black mb-2">You&apos;re in. Check your inbox.</h3>
            <p className="font-medium mb-6 text-sm">
              While you&apos;re here, help shape what we build next.
            </p>
            <a
              href={SURVEY_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="waitlist-survey-link"
              className="nb-button inline-flex items-center gap-2 bg-black text-white px-5 py-3"
            >
              Take the 2-minute survey <ExternalLink className="w-4 h-4" />
            </a>
          </motion.div>
        )}
      </section>

      {/* Trust block */}
      <section className="bg-white dark:bg-zinc-900 border-t-2 border-black dark:border-zinc-700">
        <div className="max-w-5xl mx-auto px-6 py-12 grid sm:grid-cols-3 gap-6">
          {[
            "Built around the real GCSE curriculum, Maths, Biology and Chemistry across AQA, Edexcel and OCR.",
            "Your notes stay yours. We never sell your data.",
            "Made in the UK by a parent, for parents.",
          ].map((line, i) => (
            <div key={i} className="flex gap-3 items-start">
              <Shield className="w-5 h-5 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
              <p className="text-sm font-medium leading-relaxed">{line}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
